import React from "react";
import { SimpleEventSeatmap, SimpleSeatmapSection } from "../../models/Event";
import { CartEventPackageItem, CartItem, GACartItem } from "../../models/Cart";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";
import CartStore, { GACartItemKey } from "../../stores/CartStore";
import { capitalizeWords, priceFormatter } from "../../utils/helpers";
import TrashIcon from "@mui/icons-material/DeleteOutline";
import { EventPackage } from "../../models/AssignedSeating";

type Props = {
    row: {
        eventPackage: EventPackage;
        items: CartEventPackageItem[];
    };
    cartStore: CartStore | null;
};

const Row = ({
    item,
    cartStore,
}: {
    item: CartEventPackageItem;
    cartStore: CartStore | null;
}) => {
    const getPriceNode = (): React.ReactNode => {
        if (item.comp) {
            return <Chip color="warning" label="COMP" size="small" />;
        }
        if (item.price === 0) {
            return <Chip color="success" label="FREE" size="small" />;
        }
        // if (seat.redemption) {
        //     return (
        //         <Chip
        //             color="success"
        //             size="small"
        //             label={"Code: " + seat.redemption.code}
        //             onDelete={onRemoveRedemptionCode}
        //         />
        //     );
        // }
        return (
            <Typography variant="body2">
                {priceFormatter(item.price)}
            </Typography>
        );
    };

    const getDiscountNode = (): React.ReactNode => {
        if (item.comp) return null;
        if (item.discount) {
            return (
                <Typography variant="body2" color="success.main">
                    {priceFormatter(item.discount.amount)} in discounts
                    {/* {item.quantity > 1 ? " ea." : ""} */}
                </Typography>
            );
        }
        return null;
    };

    const getServiceFeeNode = (): React.ReactNode => {
        if (item.comp) return null;
        return item.serviceFees.map((it) => (
            <Typography variant="body2" color="text.secondary" key={it.id}>
                + {priceFormatter(it.amount)} {it.name}
            </Typography>
        ));
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Grid container spacing={1}>
                <Grid item xs={7}>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primaryText.main"
                    >
                        {item.eventPackagePriceGroup.name}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                        {capitalizeWords(
                            item.seat?.seatDetails ??
                                item.section?.sectionDetails ??
                                "",
                        )}
                    </Typography>
                    {getPriceNode()}
                    {getServiceFeeNode()}
                    {getDiscountNode()}
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <IconButton
                        color="error"
                        onClick={() => {
                            cartStore?.removeEventPackageFromCart(
                                item.id,
                                item.eventPackage.id,
                                item.seat?.id ?? 0,
                            );
                        }}
                    >
                        <TrashIcon />
                    </IconButton>
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                    }}
                >
                    <Typography variant="body2">
                        {priceFormatter(cartStore?.packageItemTotal(item) ?? 0)}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

// TODO: Add package section row
const PackageCartRow = ({ row, cartStore }: Props) => {
    return (
        <Card>
            <CardContent>
                <Typography
                    variant="body2"
                    color="primaryText.main"
                    fontWeight={600}
                >
                    {row.eventPackage.name}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    {row.items
                        .sort((a, b) => (a.seat?.id ?? 0) - (b.seat?.id ?? 0))
                        .map((it2, idx2) => (
                            <Grid item xs={12} key={it2.id}>
                                <Row item={it2} cartStore={cartStore} />
                                {idx2 !== row.items.length - 1 && (
                                    <Divider
                                        sx={{
                                            mt: 2,
                                        }}
                                    />
                                )}
                            </Grid>
                        ))}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default PackageCartRow;
